import type { ReactNode } from 'react'
import { useMemo, useState } from 'react'
import { Outlet } from 'react-router-dom'
import { TabsNavigation } from '@/components/Tabs/TabsNavigation'
import type { TabNavigationItem } from '@/components/Tabs/TabsNavigation'
import styles from './TabsLayout.module.css'

export interface TabsLayoutProps {
  /** Título da página */
  title: string
  /** Subtítulo da página */
  subtitle?: string
  /** Classes adicionais opcionais para o título e o subtítulo. */
  titleClassName?: string
  subtitleClassName?: string
  /** Lista de abas para navegação */
  tabs: TabNavigationItem[]
  /** Conteúdo opcional à direita do título (ex.: botão de ação) */
  headerAction?: React.ReactNode
  tabsClassName?: string
}

/** Contexto do Outlet: filhos podem definir conteúdo à direita do título (ex.: seletor na rota Lista). */
export type TabsLayoutOutletContext = {
  setHeaderAction: (node: ReactNode | null) => void
}

/**
 * Layout para páginas com abas que navegam entre rotas diferentes.
 * Renderiza o header da página, a navegação de abas e o conteúdo via Outlet.
 */
export function TabsLayout({
  title,
  subtitle,
  titleClassName,
  subtitleClassName,
  tabs,
  headerAction: headerActionProp,
  tabsClassName,
}: TabsLayoutProps) {
  const [headerSlot, setHeaderSlot] = useState<ReactNode>(null)

  const outletContext = useMemo<TabsLayoutOutletContext>(
    () => ({ setHeaderAction: setHeaderSlot }),
    []
  )

  const headerRight = headerActionProp ?? headerSlot

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <h1 className={[styles.title, titleClassName].filter(Boolean).join(' ')}>
            {title}
          </h1>
          {subtitle && (
            <p className={[styles.subtitle, subtitleClassName].filter(Boolean).join(' ')}>
              {subtitle}
            </p>
          )}
        </div>
        {headerRight ? <div className={styles.headerAction}>{headerRight}</div> : null}
      </div>

      <TabsNavigation tabs={tabs} className={tabsClassName} />

      <div className={styles.content}>
        <Outlet context={outletContext} />
      </div>
    </div>
  )
}
